import {
    AlertCircle,
    Banknote,
    Check,
    Copy,
    KeyRound,
    LogOut,
    Plus,
    RotateCcw,
    UserCheck,
    UserX,
    Users,
    X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import PageGuide from '../components/analytics/PageGuide';
import { commercialCommissionsService, sellerPortalAdminService } from '../services/api';
import './CommercialCommissions.css';

const formatCurrency = (cents) => {
    if (cents == null) return '—';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(cents / 100);
};

const formatDate = (isoDate) => {
    if (!isoDate) return '—';
    return new Date(isoDate).toLocaleDateString('it-IT');
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const parseEuroToCents = (value) => {
    const normalized = String(value || '').replace(/\s/g, '').replace(/\./g, '').replace(',', '.');
    const amount = Number(normalized);
    if (!Number.isFinite(amount) || amount <= 0) return null;
    return Math.round(amount * 100);
};

const errorMessage = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

function AccountBadge({ seller }) {
    const account = seller.sellerAccount;
    if (!account) return <span className="cc-badge cc-badge-inactive">Nessun accesso</span>;
    if (account.status === 'ACTIVE') return <span className="cc-badge cc-badge-active">Attivo</span>;
    if (account.status === 'SUSPENDED') return <span className="cc-badge cc-badge-inactive">Sospeso</span>;
    return (
        <span className="cc-badge cc-badge-calculated">
            {account.activationExpired ? 'Invito scaduto' : 'Invito inviato'}
        </span>
    );
}

function Modal({ title, icon: Icon, onClose, children, busy }) {
    return (
        <div className="cc-overlay" role="dialog" aria-modal="true">
            <div className="cc-modal cc-modal-sm">
                <div className="cc-modal-header">
                    <div className="cc-modal-title">
                        {Icon && <Icon size={18} className="cc-modal-icon" />}
                        {title}
                    </div>
                    <button className="cc-modal-close" onClick={onClose} disabled={busy} aria-label="Chiudi">
                        <X size={18} />
                    </button>
                </div>
                <div style={{ padding: '18px 22px', overflowY: 'auto' }}>{children}</div>
            </div>
        </div>
    );
}

export default function SellerPortalAdmin() {
    const [tab, setTab] = useState('sellers');
    const [sellers, setSellers] = useState([]);
    const [payouts, setPayouts] = useState([]);
    const [referrals, setReferrals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [busy, setBusy] = useState(false);

    const [inviteFor, setInviteFor] = useState(null);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteResult, setInviteResult] = useState(null);
    const [copied, setCopied] = useState(false);

    const [percentFor, setPercentFor] = useState(null);
    const [percentValue, setPercentValue] = useState('');
    const [stepDownMonths, setStepDownMonths] = useState('');
    const [stepDownValue, setStepDownValue] = useState('');

    const [payoutOpen, setPayoutOpen] = useState(false);
    const [payoutForm, setPayoutForm] = useState({ commercialAgentId: '', amount: '', paidAt: todayIso(), bankReference: '', notes: '' });
    const [reverseFor, setReverseFor] = useState(null);
    const [reverseReason, setReverseReason] = useState('');
    const [payoutAgentFilter, setPayoutAgentFilter] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [s, p, r] = await Promise.all([
                sellerPortalAdminService.getSellers(),
                sellerPortalAdminService.getPayouts(),
                sellerPortalAdminService.getReferrals(),
            ]);
            setSellers(s.data || []);
            setPayouts(p.data || []);
            setReferrals(r.data || []);
        } catch (err) {
            setError(errorMessage(err, 'Errore nel caricamento dei dati.'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const totals = useMemo(() => sellers.reduce((acc, s) => ({
        accrued: acc.accrued + (s.balance?.accruedCents || 0),
        paid: acc.paid + (s.balance?.payoutsCents || 0) + (s.balance?.legacyPaidCents || 0),
        balance: acc.balance + (s.balance?.balanceCents || 0),
    }), { accrued: 0, paid: 0, balance: 0 }), [sellers]);

    const selectedPayoutSeller = sellers.find((s) => s.id === payoutForm.commercialAgentId);
    const payoutCents = parseEuroToCents(payoutForm.amount);
    const visiblePayouts = payoutAgentFilter ? payouts.filter((p) => p.commercialAgentId === payoutAgentFilter) : payouts;

    const run = async (fn, successMessage) => {
        setBusy(true);
        setError('');
        try {
            const result = await fn();
            if (successMessage) setNotice(successMessage);
            await load();
            return result;
        } catch (err) {
            setError(errorMessage(err, 'Operazione non riuscita.'));
            return null;
        } finally {
            setBusy(false);
        }
    };

    const openInvite = (seller) => {
        setInviteFor(seller);
        setInviteEmail(seller.sellerAccount?.email || seller.email || '');
        setInviteResult(null);
        setCopied(false);
    };

    const submitInvite = async () => {
        const res = await run(() => sellerPortalAdminService.inviteSeller(inviteFor.id, { email: inviteEmail, sendEmail: true }));
        if (res?.data) setInviteResult(res.data);
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(inviteResult.link);
            setCopied(true);
        } catch {
            setCopied(false);
        }
    };

    const toggleStatus = (seller) => {
        const next = seller.sellerAccount.status === 'SUSPENDED' ? 'ACTIVE' : 'SUSPENDED';
        const verb = next === 'SUSPENDED' ? 'sospendere' : 'riattivare';
        if (!window.confirm(`Vuoi ${verb} l'accesso al portale di ${seller.name}? Lo storico provvigioni resta invariato.`)) return;
        run(() => sellerPortalAdminService.setSellerStatus(seller.id, next), next === 'SUSPENDED' ? 'Accesso sospeso e sessioni revocate.' : 'Accesso riattivato.');
    };

    const revoke = (seller) => {
        if (!window.confirm(`Disconnettere ${seller.name} da tutti i dispositivi?`)) return;
        run(() => sellerPortalAdminService.revokeSellerSessions(seller.id), 'Sessioni revocate.');
    };

    const openPercent = (seller) => {
        setPercentFor(seller);
        setPercentValue(seller.defaultCommissionPercent ?? '');
        setStepDownMonths(seller.stepDownAfterMonths ?? '');
        setStepDownValue(seller.stepDownPercent ?? '');
    };

    const submitPercent = async () => {
        const toNumber = (v) => (v === '' || v === null ? null : Number(String(v).replace(',', '.')));
        const res = await run(
            () => commercialCommissionsService.updateAgent(percentFor.id, {
                defaultCommissionPercent: toNumber(percentValue),
                stepDownAfterMonths: toNumber(stepDownMonths),
                stepDownPercent: toNumber(stepDownValue),
            }),
            'Percentuali aggiornate.'
        );
        if (res) setPercentFor(null);
    };

    const openPayout = (seller) => {
        setPayoutForm({ commercialAgentId: seller?.id || '', amount: '', paidAt: todayIso(), bankReference: '', notes: '' });
        setPayoutOpen(true);
    };

    const submitPayout = async () => {
        if (!payoutCents) { setError("Inserisci un importo valido, es. 120,50."); return; }
        if (selectedPayoutSeller && payoutCents > (selectedPayoutSeller.balance?.balanceCents || 0)
            && !window.confirm('L\'importo supera il saldo maturato del venditore: il saldo diventerà negativo (anticipo). Confermi?')) {
            return;
        }
        const res = await run(() => sellerPortalAdminService.createPayout({
            commercialAgentId: payoutForm.commercialAgentId,
            amountCents: payoutCents,
            paidAt: payoutForm.paidAt,
            bankReference: payoutForm.bankReference,
            notes: payoutForm.notes,
        }), 'Bonifico registrato.');
        if (res) setPayoutOpen(false);
    };

    const submitReverse = async () => {
        const res = await run(() => sellerPortalAdminService.reversePayout(reverseFor.id, reverseReason), 'Bonifico stornato.');
        if (res) { setReverseFor(null); setReverseReason(''); }
    };

    return (
        <div className="cc-page">
            <div className="cc-header">
                <div className="cc-header-left">
                    <Users size={26} className="cc-header-icon" />
                    <div>
                        <h1>Seller Portal</h1>
                        <p className="cc-subtitle">Accessi dei venditori, bonifici provvigionali, saldi e clienti acquisiti con referral</p>
                    </div>
                </div>
                <div className="cc-header-actions">
                    <button className="cc-btn cc-btn-primary" onClick={() => openPayout(null)} disabled={busy || sellers.length === 0}>
                        <Banknote size={15} />
                        Registra bonifico
                    </button>
                </div>
            </div>

            <PageGuide
                title="Come funziona il Seller Portal"
                summary={
                    <div className="cc-guide-content">
                        <p><strong>Accesso:</strong> crea l&apos;accesso a un commerciale: riceve un link per scegliere la password (valido 72 ore). Il link è mostrato anche qui, per inviarlo a mano se l&apos;email non arriva.</p>
                        <p><strong>Saldo:</strong> provvigioni maturate (snapshot mensili della pagina Provvigioni) meno righe già segnate come pagate meno bonifici registrati qui. Il mese in corso è provvisorio e non entra nel saldo.</p>
                        <p><strong>Referral:</strong> il codice commerciale diventa il codice referral. I nuovi tenant registrati con il codice vengono attribuiti al venditore e ricevono in automatico la regola con la sua percentuale di default (dal mese corrente).</p>
                        <p><strong>Riduzione da contratto:</strong> dal pulsante % imposti anche il passaggio automatico (es. 10% dopo 12 mesi dalla prima mancia del cliente). Il calcolo mensile e il portale la applicano da soli.</p>
                        <p className="cc-guide-note">I bonifici non si cancellano: un errore si corregge con uno storno motivato, che resta nello storico.</p>
                    </div>
                }
            />

            {error && (
                <div className="cc-alert cc-alert-error">
                    <AlertCircle size={16} />
                    <span>{error}</span>
                    <button className="cc-alert-close" onClick={() => setError('')} aria-label="Chiudi"><X size={14} /></button>
                </div>
            )}
            {notice && (
                <div className="cc-alert" style={{ background: '#ecfdf5', color: '#047857', borderColor: '#a7f3d0' }}>
                    <Check size={16} />
                    <span>{notice}</span>
                    <button className="cc-alert-close" onClick={() => setNotice('')} aria-label="Chiudi"><X size={14} /></button>
                </div>
            )}

            <div className="cc-summary-row">
                <div className="cc-summary-card"><span className="cc-summary-label">Maturato totale</span><span className="cc-summary-value">{formatCurrency(totals.accrued)}</span></div>
                <div className="cc-summary-card"><span className="cc-summary-label">Pagato totale</span><span className="cc-summary-value">{formatCurrency(totals.paid)}</span></div>
                <div className="cc-summary-card"><span className="cc-summary-label">Saldo da pagare</span><span className="cc-summary-value">{formatCurrency(totals.balance)}</span></div>
                <div className="cc-summary-card"><span className="cc-summary-label">Tenant da referral</span><span className="cc-summary-value">{referrals.length}</span></div>
            </div>

            <div className="cc-modal-tabs" style={{ padding: 0, marginBottom: 12 }}>
                {[['sellers', 'Venditori'], ['payouts', 'Bonifici'], ['referrals', 'Referral']].map(([key, label]) => (
                    <button key={key} className={`cc-tab ${tab === key ? 'cc-tab-active' : ''}`} onClick={() => setTab(key)}>{label}</button>
                ))}
            </div>

            <div className="cc-table-card">
                {loading ? (
                    <div className="cc-loading"><div className="cc-spinner" /> Caricamento…</div>
                ) : tab === 'sellers' ? (
                    sellers.length === 0 ? (
                        <div className="cc-empty"><p className="cc-empty-title">Nessun commerciale</p><p className="cc-empty-sub">Crea i commerciali dalla pagina Provvigioni.</p></div>
                    ) : (
                        <div className="cc-table-wrap">
                            <table className="cc-table">
                                <thead>
                                    <tr>
                                        <th>Commerciale</th>
                                        <th>Accesso portale</th>
                                        <th className="cc-td-right">% default referral</th>
                                        <th className="cc-td-right">Referral</th>
                                        <th className="cc-td-right">Maturato</th>
                                        <th className="cc-td-right">Pagato</th>
                                        <th className="cc-td-right">Saldo</th>
                                        <th>Azioni</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sellers.map((s) => (
                                        <tr key={s.id}>
                                            <td>
                                                <div className="cc-agent-name">{s.name}{!s.active && <span className="cc-badge cc-badge-inactive" style={{ marginLeft: 6 }}>Disattivato</span>}</div>
                                                <div className="cc-agent-code">{s.code || 'senza codice'} · {s.sellerAccount?.email || s.email || 'senza email'}</div>
                                            </td>
                                            <td>
                                                <AccountBadge seller={s} />
                                                {s.sellerAccount?.lastLoginAt && <div className="cc-field-hint">Ultimo accesso {formatDate(s.sellerAccount.lastLoginAt)}</div>}
                                            </td>
                                            <td className="cc-td-right">
                                                {s.defaultCommissionPercent == null ? '—' : `${s.defaultCommissionPercent}%`}
                                                {s.stepDownAfterMonths != null && (
                                                    <div className="cc-field-hint">poi {s.stepDownPercent}% dopo {s.stepDownAfterMonths} mesi</div>
                                                )}
                                            </td>
                                            <td className="cc-td-right">{s.referredTenantsCount}</td>
                                            <td className="cc-td-right">{formatCurrency(s.balance?.accruedCents)}</td>
                                            <td className="cc-td-right">{formatCurrency((s.balance?.payoutsCents || 0) + (s.balance?.legacyPaidCents || 0))}</td>
                                            <td className="cc-td-right"><strong>{formatCurrency(s.balance?.balanceCents)}</strong></td>
                                            <td className="cc-td-actions">
                                                <button className="cc-btn-action" title={s.sellerAccount ? 'Nuovo link accesso / reset password' : 'Crea accesso al portale'} onClick={() => openInvite(s)} disabled={busy || s.sellerAccount?.status === 'SUSPENDED'}>
                                                    <KeyRound size={14} />
                                                </button>
                                                <button className="cc-btn-action" title="Percentuale di default e riduzione da contratto" onClick={() => openPercent(s)} disabled={busy}>%</button>
                                                <button className="cc-btn-action cc-btn-pay" title="Registra bonifico" onClick={() => openPayout(s)} disabled={busy}>
                                                    <Banknote size={14} />
                                                </button>
                                                {s.sellerAccount && s.sellerAccount.status !== 'INVITED' && (
                                                    <button className={`cc-btn-action ${s.sellerAccount.status === 'SUSPENDED' ? 'cc-btn-activate' : 'cc-btn-deactivate'}`} title={s.sellerAccount.status === 'SUSPENDED' ? 'Riattiva accesso' : 'Sospendi accesso'} onClick={() => toggleStatus(s)} disabled={busy}>
                                                        {s.sellerAccount.status === 'SUSPENDED' ? <UserCheck size={14} /> : <UserX size={14} />}
                                                    </button>
                                                )}
                                                {s.sellerAccount?.status === 'ACTIVE' && (
                                                    <button className="cc-btn-action" title="Disconnetti da tutti i dispositivi" onClick={() => revoke(s)} disabled={busy}>
                                                        <LogOut size={14} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )
                ) : tab === 'payouts' ? (
                    <>
                        <div style={{ padding: '12px 16px' }}>
                            <select className="cc-select" value={payoutAgentFilter} onChange={(e) => setPayoutAgentFilter(e.target.value)} aria-label="Filtra per commerciale">
                                <option value="">Tutti i commerciali</option>
                                {sellers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                        </div>
                        {visiblePayouts.length === 0 ? (
                            <div className="cc-empty"><p className="cc-empty-title">Nessun bonifico registrato</p></div>
                        ) : (
                            <div className="cc-table-wrap">
                                <table className="cc-table">
                                    <thead>
                                        <tr>
                                            <th>Data</th>
                                            <th>Commerciale</th>
                                            <th className="cc-td-right">Importo</th>
                                            <th>Riferimento</th>
                                            <th>Note</th>
                                            <th>Stato</th>
                                            <th>Azioni</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visiblePayouts.map((p) => (
                                            <tr key={p.id} style={p.status === 'REVERSED' ? { opacity: 0.6 } : undefined}>
                                                <td>{formatDate(p.paidAt)}</td>
                                                <td>{p.agentName}</td>
                                                <td className="cc-td-right">{formatCurrency(p.amountCents)}</td>
                                                <td>{p.bankReference || '—'}</td>
                                                <td>{p.notes || '—'}</td>
                                                <td>
                                                    {p.status === 'REVERSED'
                                                        ? <span className="cc-badge cc-badge-inactive" title={p.reversalReason || ''}>Stornato {formatDate(p.reversedAt)}</span>
                                                        : <span className="cc-badge cc-badge-paid">Valido</span>}
                                                </td>
                                                <td className="cc-td-actions">
                                                    {p.status === 'VALID' && (
                                                        <button className="cc-btn-action cc-btn-deactivate" title="Storna bonifico" onClick={() => { setReverseFor(p); setReverseReason(''); }} disabled={busy}>
                                                            <RotateCcw size={14} />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                ) : referrals.length === 0 ? (
                    <div className="cc-empty"><p className="cc-empty-title">Nessun tenant acquisito con referral</p></div>
                ) : (
                    <div className="cc-table-wrap">
                        <table className="cc-table">
                            <thead>
                                <tr><th>Tenant</th><th>Stato</th><th>Commerciale</th><th>Codice usato</th><th>Data</th></tr>
                            </thead>
                            <tbody>
                                {referrals.map((t) => (
                                    <tr key={t.id}>
                                        <td className="cc-td-tenant">{t.adminAlias || t.company_name}</td>
                                        <td>{t.status}</td>
                                        <td>{t.referredByCommercialAgent?.name || '—'}</td>
                                        <td>{t.referralCodeUsed || '—'}</td>
                                        <td>{formatDate(t.referredAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {inviteFor && (
                <Modal title={inviteFor.sellerAccount ? 'Nuovo link di accesso' : 'Crea accesso al portale'} icon={KeyRound} onClose={() => setInviteFor(null)} busy={busy}>
                    {inviteResult ? (
                        <>
                            <p className="cc-form-note">
                                {inviteResult.emailSent ? `Email inviata a ${inviteResult.email}.` : `Email non inviata: manda tu il link a ${inviteResult.email}.`}
                                {' '}Il link vale fino al {new Date(inviteResult.expiresAt).toLocaleString('it-IT')}.
                            </p>
                            <div className="cc-form-field">
                                <label htmlFor="seller-link">{inviteResult.purpose === 'reset' ? 'Link reset password' : 'Link di attivazione'}</label>
                                <input id="seller-link" type="text" readOnly value={inviteResult.link} onFocus={(e) => e.target.select()} />
                            </div>
                            <div className="cc-form-actions" style={{ marginTop: 14 }}>
                                <button className="cc-btn cc-btn-ghost" onClick={copyLink}><Copy size={14} /> {copied ? 'Copiato' : 'Copia link'}</button>
                                <button className="cc-btn cc-btn-primary" onClick={() => setInviteFor(null)}>Fatto</button>
                            </div>
                        </>
                    ) : (
                        <>
                            <p className="cc-form-note">
                                {inviteFor.sellerAccount?.status === 'ACTIVE'
                                    ? `${inviteFor.name} ha già un accesso: genera un link per reimpostare la password.`
                                    : `${inviteFor.name} riceverà un link per scegliere la password del Seller Portal.`}
                            </p>
                            <div className="cc-form-grid">
                                <div className="cc-form-field">
                                    <label htmlFor="seller-email">Email di accesso</label>
                                    <input id="seller-email" type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} disabled={inviteFor.sellerAccount?.status === 'ACTIVE'} />
                                    {!inviteFor.code && <span className="cc-field-hint">Il commerciale non ha un codice: senza codice non potrà usare il referral.</span>}
                                </div>
                            </div>
                            <div className="cc-form-actions">
                                <button className="cc-btn cc-btn-ghost" onClick={() => setInviteFor(null)} disabled={busy}>Annulla</button>
                                <button className="cc-btn cc-btn-primary" onClick={submitInvite} disabled={busy || !inviteEmail}>
                                    {inviteFor.sellerAccount?.status === 'ACTIVE' ? 'Genera link reset' : 'Crea e invia'}
                                </button>
                            </div>
                        </>
                    )}
                </Modal>
            )}

            {percentFor && (
                <Modal title={`Percentuali · ${percentFor.name}`} onClose={() => setPercentFor(null)} busy={busy}>
                    <p className="cc-form-note"><strong>Default referral:</strong> applicata in automatico ai nuovi tenant registrati con il codice {percentFor.code || '(nessun codice)'}. Non modifica le regole già esistenti. Lascia vuoto per non creare regole automatiche.</p>
                    <div className="cc-form-grid">
                        <div className="cc-form-field">
                            <label htmlFor="default-percent">Percentuale sulla Fee Soontip (0–20)</label>
                            <input id="default-percent" type="number" min="0" max="20" step="0.5" value={percentValue} onChange={(e) => setPercentValue(e.target.value)} />
                        </div>
                    </div>
                    <p className="cc-form-note"><strong>Riduzione da contratto:</strong> vale per tutti i clienti di questo venditore, anche quelli già assegnati. I mesi si contano dalla prima mancia andata a buon fine del cliente (il mese della prima mancia è il mese 1). Esempio: 12 mesi e 10% = 20% per i primi 12 mesi, 10% dal 13°. Non alza mai una percentuale più bassa. Lascia vuoti i due campi per non applicare riduzioni.</p>
                    <div className="cc-form-grid">
                        <div className="cc-form-field">
                            <label htmlFor="stepdown-months">Dopo quanti mesi</label>
                            <input id="stepdown-months" type="number" min="1" max="120" step="1" value={stepDownMonths} onChange={(e) => setStepDownMonths(e.target.value)} />
                        </div>
                        <div className="cc-form-field">
                            <label htmlFor="stepdown-percent">Percentuale ridotta (0–20)</label>
                            <input id="stepdown-percent" type="number" min="0" max="20" step="0.5" value={stepDownValue} onChange={(e) => setStepDownValue(e.target.value)} />
                        </div>
                    </div>
                    <div className="cc-form-actions">
                        <button className="cc-btn cc-btn-ghost" onClick={() => setPercentFor(null)} disabled={busy}>Annulla</button>
                        <button className="cc-btn cc-btn-primary" onClick={submitPercent} disabled={busy}>Salva</button>
                    </div>
                </Modal>
            )}

            {payoutOpen && (
                <Modal title="Registra bonifico" icon={Banknote} onClose={() => setPayoutOpen(false)} busy={busy}>
                    <p className="cc-form-note">Registra un bonifico già eseguito dalla banca. Può essere parziale o coprire più mesi.</p>
                    <div className="cc-form-grid">
                        <div className="cc-form-field">
                            <label htmlFor="payout-agent">Commerciale *</label>
                            <select id="payout-agent" value={payoutForm.commercialAgentId} onChange={(e) => setPayoutForm((f) => ({ ...f, commercialAgentId: e.target.value }))}>
                                <option value="">Seleziona…</option>
                                {sellers.map((s) => <option key={s.id} value={s.id}>{s.name} (saldo {formatCurrency(s.balance?.balanceCents)})</option>)}
                            </select>
                        </div>
                        <div className="cc-form-field">
                            <label htmlFor="payout-amount">Importo (€) *</label>
                            <input id="payout-amount" type="text" inputMode="decimal" placeholder="es. 120,50" value={payoutForm.amount} onChange={(e) => setPayoutForm((f) => ({ ...f, amount: e.target.value }))} />
                            {selectedPayoutSeller && (
                                <button type="button" className="cc-btn cc-btn-ghost cc-btn-sm" style={{ alignSelf: 'flex-start' }}
                                    onClick={() => setPayoutForm((f) => ({ ...f, amount: ((selectedPayoutSeller.balance?.balanceCents || 0) / 100).toFixed(2).replace('.', ',') }))}
                                    disabled={(selectedPayoutSeller.balance?.balanceCents || 0) <= 0}>
                                    <Plus size={12} /> Usa saldo intero ({formatCurrency(selectedPayoutSeller.balance?.balanceCents)})
                                </button>
                            )}
                        </div>
                        <div className="cc-form-field">
                            <label htmlFor="payout-date">Data bonifico *</label>
                            <input id="payout-date" type="date" max={todayIso()} value={payoutForm.paidAt} onChange={(e) => setPayoutForm((f) => ({ ...f, paidAt: e.target.value }))} />
                        </div>
                        <div className="cc-form-field">
                            <label htmlFor="payout-ref">Riferimento (CRO/TRN)</label>
                            <input id="payout-ref" type="text" value={payoutForm.bankReference} onChange={(e) => setPayoutForm((f) => ({ ...f, bankReference: e.target.value }))} />
                            <span className="cc-field-hint">Visibile al venditore nel portale.</span>
                        </div>
                        <div className="cc-form-field">
                            <label htmlFor="payout-notes">Nota per il venditore</label>
                            <input id="payout-notes" type="text" placeholder="Es. provvigioni di settembre, locale Centro" value={payoutForm.notes} onChange={(e) => setPayoutForm((f) => ({ ...f, notes: e.target.value }))} />
                            <span className="cc-field-hint">Visibile al venditore nel portale.</span>
                        </div>
                    </div>
                    <div className="cc-form-actions">
                        <button className="cc-btn cc-btn-ghost" onClick={() => setPayoutOpen(false)} disabled={busy}>Annulla</button>
                        <button className="cc-btn cc-btn-primary" onClick={submitPayout} disabled={busy || !payoutForm.commercialAgentId || !payoutCents || !payoutForm.paidAt}>
                            Registra {payoutCents ? formatCurrency(payoutCents) : ''}
                        </button>
                    </div>
                </Modal>
            )}

            {reverseFor && (
                <Modal title="Storna bonifico" icon={RotateCcw} onClose={() => setReverseFor(null)} busy={busy}>
                    <p className="cc-form-note">
                        Bonifico di {formatCurrency(reverseFor.amountCents)} del {formatDate(reverseFor.paidAt)} a {reverseFor.agentName}.
                        Lo storno resta nello storico e il saldo del venditore torna a includere questo importo.
                    </p>
                    <div className="cc-form-grid">
                        <div className="cc-form-field">
                            <label htmlFor="reverse-reason">Motivo *</label>
                            <input id="reverse-reason" type="text" value={reverseReason} onChange={(e) => setReverseReason(e.target.value)} />
                        </div>
                    </div>
                    <div className="cc-form-actions">
                        <button className="cc-btn cc-btn-ghost" onClick={() => setReverseFor(null)} disabled={busy}>Annulla</button>
                        <button className="cc-btn cc-btn-primary" onClick={submitReverse} disabled={busy || !reverseReason.trim()}>Storna</button>
                    </div>
                </Modal>
            )}
        </div>
    );
}
